import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RenderingCancelledException, getDocument } from 'pdfjs-dist';
/**
 * Creates a debounced function that delays invoking the provided functions until after the specified delay has elapsed
 * since the last time the debounced function was invoked.
 */
const debounce = (delay, ...funcs) => {
    let timeoutId;
    return () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            funcs.forEach(func => func());
        }, delay);
    };
};
// Custom hook to manage page text content loading
export const usePDFPages = ({ pdfUrl, pdfScale, onRenderComplete, onError, ref, }) => {
    const [pagesTextContent, setPagesTextContent] = useState([]);
    const [error, setError] = useState(null);
    const renderTasks = useRef([]);
    const pdf = React.useRef();
    // load PDF's content
    const loadPDF = useCallback(async () => {
        try {
            if (!ref.current) {
                return;
            }
            const loadingTask = getDocument(pdfUrl);
            pdf.current = await loadingTask.promise;
            let viewportWidth = 0;
            const loadedPages = [];
            const pdfViewerRoot = ref.current;
            const desiredWidth = pdfViewerRoot?.parentElement.clientWidth || window.innerWidth;
            for (let pageNum = 1; pageNum <= pdf.current.numPages; pageNum++) {
                // load page content adapting the size to parent element or window width
                const page = await pdf.current.getPage(pageNum);
                const vp = page.getViewport({ scale: 1 });
                const scale = (desiredWidth / vp.width) * pdfScale;
                const viewport = page.getViewport({ scale: scale });
                if (viewportWidth === 0) {
                    viewportWidth = viewport.width;
                }
                const textContent = await page.getTextContent();
                loadedPages.push({ textContent, viewport });
            }
            setPagesTextContent(loadedPages);
        }
        catch (error) {
            setError(error);
            onError?.(error);
        }
    }, [pdfUrl, pdfScale]);
    useEffect(() => {
        loadPDF();
    }, [loadPDF]);
    // paint PDF on canvas
    const paintPDF = useCallback(async () => {
        try {
            if (!ref.current || !pdf.current || pagesTextContent.length === 0) {
                return;
            }
            // Cancel any ongoing render tasks, if not, might lead to unexpected behaviour
            renderTasks.current.forEach(task => task.cancel());
            renderTasks.current = [];
            const canvasList = ref.current.querySelectorAll('canvas');
            const renderPromises = pagesTextContent.map(async (pageContent, pageIndex) => {
                const canvas = canvasList[pageIndex];
                if (!pdf.current || !canvas) {
                    return;
                }
                const context = canvas.getContext('2d');
                if (!context) {
                    return;
                }
                const page = await pdf.current.getPage(pageIndex + 1);
                const { viewport } = pageContent;
                // Adjust canvas size based on device pixel ratio
                const devicePixelRatio = window.devicePixelRatio || 1;
                canvas.width = Math.floor(viewport.width * devicePixelRatio);
                canvas.height = Math.floor(viewport.height * devicePixelRatio);
                canvas.style.width = Math.floor(viewport.width) + 'px';
                canvas.style.height = Math.floor(viewport.height) + 'px';
                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                    transform: [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0], // Scale the rendering
                };
                const renderTask = page.render(renderContext);
                renderTasks.current.push(renderTask);
                return renderTask.promise;
            });
            await Promise.all(renderPromises);
            onRenderComplete?.(pagesTextContent[0].viewport.width);
        }
        catch (error) {
            if (error instanceof RenderingCancelledException) {
                // Render task cancelled, no action needed
            }
            else {
                setError(error);
                onError?.(error);
            }
        }
    }, [pagesTextContent]);
    useEffect(() => {
        paintPDF();
        // Debounce the resize event to avoid calling loadPDF and paintPDF too many times
        const debouncedLoad = debounce(300, loadPDF);
        window.addEventListener('resize', debouncedLoad);
        return () => {
            window.removeEventListener('resize', debouncedLoad);
        };
        // ref is not a dependency because it is a ref
    }, [paintPDF]);
    return { pagesTextContent, error };
};
//# sourceMappingURL=usePDFPages.js.map